import { useEffect, useState } from 'react'
import { api, CfdiDelRepositorio, TipoComprobante } from '../servicios/api'

/**
 * Visor legible de un CFDI, con las tres pestañas del prototipo aprobado
 * (docs/design/ContaQuick-Prototipo.html): Factura, Campos y XML. SRS: RF-506. Cédula: D-07.
 *
 * Vive fuera de `paginas/` porque lo usan dos pantallas con envoltorios distintos: el
 * repositorio lo abre dentro de un modal y la contabilización lo monta como columna fija.
 * Por eso el componente NO trae `.modal`, ni trampa de foco, ni botón de cerrar: quien lo
 * usa decide si es un diálogo. Antes era las dos cosas a la vez y solo servía en el modal.
 */

/** Lo que el visor necesita de un CFDI, venga del repositorio o del panel de contabilización. */
export type CfdiVisible = Pick<
  CfdiDelRepositorio,
  | 'id' | 'uuid' | 'tipo' | 'serie' | 'folio' | 'fechaEmision'
  | 'rfcEmisor' | 'nombreEmisor' | 'rfcReceptor' | 'nombreReceptor'
  | 'total' | 'moneda' | 'esEmitido'
>

export type PestanaDelVisor = 'factura' | 'campos' | 'xml'

interface ConceptoLeido {
  descripcion: string
  cantidad: string
  valorUnitario: string
  importe: string
}

/**
 * Los cinco campos que ningún DTO expone todavía (subtotal, impuestos, forma y método de
 * pago, uso del CFDI) sí están en el XML que el visor ya descargaba para la descarga.
 * Leerlos aquí completa la ficha hoy; el día que la API los proyecte, basta preferir el DTO.
 */
interface LecturaDelXml {
  conceptos: ConceptoLeido[]
  subTotal?: number
  impuestos?: number
  formaPago?: string
  metodoPago?: string
  usoCfdi?: string
}

const numero = (v: string | null) => {
  if (!v) return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

function leerXml(texto: string): LecturaDelXml {
  const doc = new DOMParser().parseFromString(texto, 'application/xml')
  const comprobante = doc.getElementsByTagNameNS('*', 'Comprobante')[0]
  const receptor = doc.getElementsByTagNameNS('*', 'Receptor')[0]
  const impuestos = doc.getElementsByTagNameNS('*', 'Impuestos')[0]

  const trasladados = numero(impuestos?.getAttribute('TotalImpuestosTrasladados') ?? null)
  const retenidos = numero(impuestos?.getAttribute('TotalImpuestosRetenidos') ?? null)

  return {
    conceptos: Array.from(doc.getElementsByTagNameNS('*', 'Concepto')).map((n) => ({
      descripcion: n.getAttribute('Descripcion') ?? '',
      cantidad: n.getAttribute('Cantidad') ?? '',
      valorUnitario: n.getAttribute('ValorUnitario') ?? '',
      importe: n.getAttribute('Importe') ?? '',
    })),
    subTotal: numero(comprobante?.getAttribute('SubTotal') ?? null),
    impuestos: trasladados === undefined && retenidos === undefined
      ? undefined
      : (trasladados ?? 0) - (retenidos ?? 0),
    formaPago: comprobante?.getAttribute('FormaPago') ?? undefined,
    metodoPago: comprobante?.getAttribute('MetodoPago') ?? undefined,
    usoCfdi: receptor?.getAttribute('UsoCFDI') ?? undefined,
  }
}

const dinero = (n: number) =>
  n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const NOMBRE_DE_TIPO: Record<TipoComprobante, string> = {
  Ingreso: 'Ingreso', Egreso: 'Egreso', Pago: 'Pago', Nomina: 'Nómina', Traslado: 'Traslado',
}

const serieFolio = (cfdi: CfdiVisible) =>
  [cfdi.serie, cfdi.folio].filter(Boolean).join(' / ') || '—'

export interface PropiedadesDelVisorDeCfdi {
  clienteId: string
  cfdi: CfdiVisible
  /** Estatus ante el SAT (RF-505). Solo el repositorio lo conoce; el panel lo omite. */
  estatus?: 'Vigente' | 'Cancelado'
  /** Uso del CFDI según el DTO; si no viene, se lee de `cfdi:Receptor@UsoCFDI`. */
  usoCfdi?: string
  pestanaInicial?: PestanaDelVisor
}

export function VisorDeCfdi({ clienteId, cfdi, estatus, usoCfdi, pestanaInicial }: PropiedadesDelVisorDeCfdi) {
  const [pestana, setPestana] = useState<PestanaDelVisor>(pestanaInicial ?? 'factura')
  const [lectura, setLectura] = useState<LecturaDelXml | null>(null)
  const [xml, setXml] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    // Como columna fija el visor cambia de CFDI sin desmontarse: sin esta guarda, una
    // respuesta lenta de un comprobante ya abandonado pisaría la del que está en pantalla.
    let vigente = true
    setLectura(null)
    setXml('')
    setError('')
    api.obtenerXmlCfdi(clienteId, cfdi.id)
      .then((texto) => {
        if (!vigente) return
        setXml(texto)
        setLectura(leerXml(texto))
      })
      .catch(() => { if (vigente) setError('No se pudo leer el XML original.') })
    return () => { vigente = false }
  }, [clienteId, cfdi.id])

  function descargarXml() {
    const enlace = document.createElement('a')
    enlace.href = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }))
    enlace.download = `${cfdi.uuid}.xml`
    enlace.click()
    URL.revokeObjectURL(enlace.href)
  }

  const tipoLargo = `${cfdi.tipo.charAt(0)} · ${NOMBRE_DE_TIPO[cfdi.tipo]} (${cfdi.esEmitido ? 'emitido' : 'recibido'})`
  const uso = usoCfdi ?? lectura?.usoCfdi
  const fechaLarga = new Date(cfdi.fechaEmision).toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })

  return (
    <>
      <div className="pestanas suave" role="tablist" aria-label="Vistas del comprobante">
        {(['factura', 'campos', 'xml'] as PestanaDelVisor[]).map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            className="pestana"
            aria-selected={pestana === p}
            onClick={() => setPestana(p)}
          >
            {p === 'factura' ? 'Factura' : p === 'campos' ? 'Campos' : 'XML'}
          </button>
        ))}
        <button type="button" className="enlace a-la-derecha" onClick={descargarXml} disabled={!xml}>
          Descargar XML
        </button>
      </div>

      {error && <p className="error" role="alert">{error}</p>}

      {pestana === 'factura' && (
        <div className="detalle-cuerpo">
          <div className="par-emisor-receptor">
            <div>
              <div className="kicker">Emisor</div>
              <div className="nombre-parte">{cfdi.nombreEmisor || '—'}</div>
              <div className="mono">{cfdi.rfcEmisor}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="kicker">Receptor</div>
              <div className="nombre-parte">{cfdi.nombreReceptor || '—'}</div>
              <div className="mono">{cfdi.rfcReceptor}</div>
            </div>
          </div>

          <div className="meta-cfdi">
            <span><strong>Serie/folio:</strong> {serieFolio(cfdi)}</span>
            <span><strong>Fecha:</strong> {fechaLarga}</span>
            <span><strong>Tipo:</strong> {tipoLargo}</span>
            {uso && <span><strong>Uso:</strong> {uso}</span>}
            {estatus && (
              <span>
                <strong>Estatus SAT:</strong>{' '}
                <span className={`insignia ${estatus === 'Cancelado' ? 'vencido' : 'vigente'}`}>{estatus}</span>
              </span>
            )}
          </div>

          {lectura === null && !error ? (
            <p className="sutil">Leyendo el XML original…</p>
          ) : lectura && lectura.conceptos.length > 0 ? (
            <table className="tabla densa">
              <thead>
                <tr>
                  <th>Concepto</th><th className="num">Cant.</th>
                  <th className="num">P. unitario</th><th className="num">Importe</th>
                </tr>
              </thead>
              <tbody>
                {lectura.conceptos.map((c, i) => (
                  <tr key={i}>
                    <td>{c.descripcion}</td>
                    <td className="mono num">{c.cantidad}</td>
                    <td className="mono num">{c.valorUnitario}</td>
                    <td className="mono num">{c.importe}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          <div className="totales">
            {lectura?.subTotal !== undefined && (
              <span className="renglon"><span>Subtotal</span><span className="num">{dinero(lectura.subTotal)}</span></span>
            )}
            {lectura?.impuestos !== undefined && (
              <span className="renglon"><span>Impuestos</span><span className="num">{dinero(lectura.impuestos)}</span></span>
            )}
            <span className="renglon total">
              <span>Total {cfdi.moneda}</span><span className="num">{dinero(cfdi.total)}</span>
            </span>
          </div>

          <div className="uuid-pie">UUID {cfdi.uuid}</div>
        </div>
      )}

      {pestana === 'campos' && (
        <div className="rejilla-campos">
          <div className="dato"><span>UUID</span><span className="mono">{cfdi.uuid}</span></div>
          <div className="dato"><span>Tipo de comprobante</span><span>{tipoLargo}</span></div>
          <div className="dato"><span>RFC emisor</span><span className="mono">{cfdi.rfcEmisor}</span></div>
          <div className="dato"><span>RFC receptor</span><span className="mono">{cfdi.rfcReceptor}</span></div>
          <div className="dato"><span>Serie / folio</span><span>{serieFolio(cfdi)}</span></div>
          <div className="dato"><span>Fecha de emisión</span><span>{fechaLarga}</span></div>
          <div className="dato">
            <span>Subtotal</span>
            <span className="num">{lectura?.subTotal !== undefined ? dinero(lectura.subTotal) : '—'}</span>
          </div>
          <div className="dato">
            <span>Impuestos</span>
            <span className="num">{lectura?.impuestos !== undefined ? dinero(lectura.impuestos) : '—'}</span>
          </div>
          <div className="dato">
            <span>Total</span><span className="num-fuerte">{dinero(cfdi.total)} {cfdi.moneda}</span>
          </div>
          <div className="dato"><span>Forma de pago</span><span>{lectura?.formaPago ?? '—'}</span></div>
          <div className="dato"><span>Método de pago</span><span>{lectura?.metodoPago ?? '—'}</span></div>
          <div className="dato"><span>Uso CFDI</span><span>{uso ?? '—'}</span></div>
          {estatus && (
            <div className="dato">
              <span>Estatus ante el SAT</span>
              <span className={`insignia ${estatus === 'Cancelado' ? 'vencido' : 'vigente'}`}>{estatus}</span>
            </div>
          )}
          <p className="nota-ancha">
            Todos los campos son indexados y buscables; el estatus ante el SAT se verifica periódicamente.
          </p>
        </div>
      )}

      {pestana === 'xml' && (
        xml
          ? <pre className="visor-xml">{xml}</pre>
          : !error && <p className="sutil">Leyendo el XML original…</p>
      )}
    </>
  )
}
